import { useState } from 'react'
import { useGameStore, Item, ItemRarity } from '@store/gameStore'
import { GAME_CONFIG } from '@config/gameConfig'
import { GameLayout } from '@components/GameLayout'
import './GachaScreen.css'

interface LootItem {
    id: string
    name: string
    emoji: string
    rarity: string
    power: number
    gold?: number
}

const LOOT_TABLE: LootItem[] = [
    { id: 'common1', name: 'Rostiges Schwert', emoji: '🗡️', rarity: 'common', power: 5 },
    { id: 'common2', name: 'Stoffrüstung', emoji: '👕', rarity: 'common', power: 5 },
    { id: 'common3', name: 'Lederhelm', emoji: '🧢', rarity: 'common', power: 3 },
    { id: 'common4', name: '100 Gold', emoji: '💰', rarity: 'common', power: 0, gold: 100 },
    { id: 'uncommon1', name: 'Stählernes Schwert', emoji: '⚔️', rarity: 'uncommon', power: 15 },
    { id: 'uncommon2', name: 'Kettenhemd', emoji: '🥋', rarity: 'uncommon', power: 12 },
    { id: 'uncommon3', name: '500 Gold', emoji: '💰', rarity: 'uncommon', power: 0, gold: 500 },
    { id: 'rare1', name: 'Magisches Schwert', emoji: '✨', rarity: 'rare', power: 35 },
    { id: 'rare2', name: 'Drachenrüstung', emoji: '🐉', rarity: 'rare', power: 30 },
    { id: 'epic1', name: 'Flammenklinge', emoji: '🔥', rarity: 'epic', power: 75 },
    { id: 'epic2', name: 'Schattenmantel', emoji: '🌑', rarity: 'epic', power: 60 },
    { id: 'legendary1', name: 'Excalibur', emoji: '⚡', rarity: 'legendary', power: 200 },
]

const RARITY_COLORS: Record<string, string> = {
    common: '#9ca3af',
    uncommon: '#22c55e',
    rare: '#3b82f6',
    epic: '#a855f7',
    legendary: '#f59e0b',
}

const RARITY_NAMES: Record<string, string> = {
    common: 'Gewöhnlich',
    uncommon: 'Ungewöhnlich',
    rare: 'Selten',
    epic: 'Episch',
    legendary: 'LEGENDÄR',
}

const rollLoot = (): LootItem => {
    const roll = Math.random()
    const rates = GAME_CONFIG.gacha.dropRates
    let rarity: string

    if (roll < rates.legendary) rarity = 'legendary'
    else if (roll < rates.legendary + rates.epic) rarity = 'epic'
    else if (roll < rates.legendary + rates.epic + rates.rare) rarity = 'rare'
    else if (roll < rates.legendary + rates.epic + rates.rare + rates.uncommon) rarity = 'uncommon'
    else rarity = 'common'

    const items = LOOT_TABLE.filter(i => i.rarity === rarity)
    return items[Math.floor(Math.random() * items.length)]
}

export function GachaScreen() {
    const [isOpening, setIsOpening] = useState(false)
    const [results, setResults] = useState<LootItem[]>([])
    const [showResult, setShowResult] = useState(false)
    const [lastPullType, setLastPullType] = useState<1 | 10>(1)
    const [revealedIndex, setRevealedIndex] = useState(0)

    // Slot machine states
    const [slotBand, setSlotBand] = useState<LootItem[]>([])
    const [isSpinning, setIsSpinning] = useState(false)
    const [showSlotMachine, setShowSlotMachine] = useState(false)
    const [bandTransform, setBandTransform] = useState(0)

    const resources = useGameStore(state => state.resources)
    const spendGems = useGameStore(state => state.spendGems)
    const addGold = useGameStore(state => state.addGold)
    const setScreen = useGameStore(state => state.setScreen)
    const setShopTab = useGameStore(state => state.setShopTab)
    const addItem = useGameStore(state => state.addItem)

    // Persistent Pity
    const gachaPity = useGameStore(state => state.gachaPity)
    const setGachaPity = useGameStore(state => state.setGachaPity)

    const freeLootboxSpins = useGameStore(state => state.freeLootboxSpins) || 0
    const useFreeLootboxSpin = useGameStore(state => state.useFreeLootboxSpin)

    // Tutorial integration
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)
    const setTutorialSubStep = useGameStore(state => state.setTutorialSubStep)
    const setTutorialWonItem = useGameStore(state => state.setTutorialWonItem)

    const singlePullCost = 100
    const tenPullCost = 900

    // Helper to determine item slot based on item name/emoji
    const determineSlot = (loot: LootItem): string => {
        const name = loot.name.toLowerCase()
        if (name.includes('schwert') || name.includes('waffe') || name.includes('dolch')) return 'weapon'
        if (name.includes('helm') || name.includes('krone')) return 'head'
        if (name.includes('rüstung') || name.includes('panzer')) return 'armor'
        if (name.includes('schuh') || name.includes('stiefel')) return 'shoes'
        if (name.includes('umhang') || name.includes('cape')) return 'cape'
        if (name.includes('ring') || name.includes('amulett')) return 'ring'
        // Default slots based on emoji
        if (loot.emoji === '⚔️' || loot.emoji === '🔪' || loot.emoji === '🗡️') return 'weapon'
        if (loot.emoji === '🛡️' || loot.emoji === '🪖') return 'armor'
        return 'weapon' // Default fallback
    }

    // Generate slot machine band with near-miss psychology
    const generateSlotBand = (wonItem: LootItem): LootItem[] => {
        const band: LootItem[] = []
        const CENTER = 25
        const legendaryItems = LOOT_TABLE.filter(i => i.rarity === 'legendary')
        const epicItems = LOOT_TABLE.filter(i => i.rarity === 'epic')

        // Fill positions before won item
        for (let i = 0; i < CENTER; i++) {
            if (i === CENTER - 1) {
                // Just before: add legendary
                band.push(legendaryItems[Math.floor(Math.random() * legendaryItems.length)])
            } else if (i === CENTER - 2) {
                // Two before: add epic
                band.push(epicItems[Math.floor(Math.random() * epicItems.length)])
            } else {
                band.push(rollLoot())
            }
        }

        // Place won item at center
        band.push(wonItem)

        // Fill positions after won item
        for (let i = CENTER + 1; i < 50; i++) {
            if (i === CENTER + 1) {
                // Just after: add legendary
                band.push(legendaryItems[Math.floor(Math.random() * legendaryItems.length)])
            } else if (i === CENTER + 2) {
                // Two after: add epic
                band.push(epicItems[Math.floor(Math.random() * epicItems.length)])
            } else {
                band.push(rollLoot())
            }
        }

        return band
    }

    const performPull = (count: 1 | 10) => {
        setGachaPity(gachaPity + count)
        setLastPullType(count)

        if (tutorialPhase === 'gacha') {
            setTutorialSubStep(1)
        }

        // Roll all items
        const lootItems: LootItem[] = []
        for (let i = 0; i < count; i++) {
            // Tutorial: Rig the roll to guarantee a nice weapon
            if (tutorialPhase === 'gacha' && i === 0) {
                lootItems.push({
                    id: 'tutorial-weapon',
                    name: 'Heldenschwert',
                    emoji: '⚔️',
                    rarity: 'uncommon',
                    power: 25
                })
            } else {
                lootItems.push(rollLoot())
            }
        }

        if (count === 1) {
            // Single pull: use slot machine
            const wonItem = lootItems[0]

            // Constants for slot machine layout
            const ITEM_WIDTH = 140
            const GAP = 12
            const ITEM_WITH_GAP = ITEM_WIDTH + GAP // 152px
            const BAND_PADDING = 20
            const WON_ITEM_INDEX = 25 // Item will be placed at this index

            // Generate band and place won item at WON_ITEM_INDEX
            const band = generateSlotBand(wonItem)
            setSlotBand(band)
            setShowSlotMachine(true)
            setShowResult(false)
            setIsSpinning(true)

            // Calculate where to position the band so item at WON_ITEM_INDEX is centered
            // Item left edge: BAND_PADDING + (WON_ITEM_INDEX * ITEM_WITH_GAP)
            // Item center: item left edge + (ITEM_WIDTH / 2)
            const itemLeftEdge = BAND_PADDING + (WON_ITEM_INDEX * ITEM_WITH_GAP)
            const itemCenterInBand = itemLeftEdge + (ITEM_WIDTH / 2)

            // Use container width (max-width: 900px on desktop) instead of screen width
            const containerWidth = Math.min(window.innerWidth, 900)
            const containerCenter = containerWidth / 2

            // Move band so item center aligns with container center
            const finalTransform = containerCenter - itemCenterInBand

            // Debug logging
            console.log('🎰 Slot Machine Debug:')
            console.log('Won Item:', wonItem.name, wonItem.emoji)
            console.log('Item at band[25]:', band[WON_ITEM_INDEX].name, band[WON_ITEM_INDEX].emoji)
            console.log('Container Width:', containerWidth, 'Container Center:', containerCenter)
            console.log('Item Center in Band:', itemCenterInBand, 'Final Transform:', finalTransform)
            // Start with offset to the right
            setBandTransform(300)

            // Begin spinning animation
            setTimeout(() => {
                setBandTransform(finalTransform)
            }, 100)

            // Stop spinning class
            setTimeout(() => {
                setIsSpinning(false)
            }, 2800)

            // Apply rewards - use the exact item from the band at WON_ITEM_INDEX
            setTimeout(() => {
                const actualItem = band[WON_ITEM_INDEX]

                if (actualItem.power > 0) {
                    const item: Item = {
                        id: `loot_${Date.now()}_${Math.random()}`,
                        name: actualItem.name,
                        emoji: actualItem.emoji,
                        slot: determineSlot(actualItem),
                        power: actualItem.power,
                        rarity: actualItem.rarity as ItemRarity
                    }
                    addItem(item)
                }

                if (actualItem.gold) addGold(actualItem.gold)

                setShowSlotMachine(false)
                setResults([actualItem])
                setShowResult(true)
                setRevealedIndex(1)

                // Tutorial: Store won item and prepare for hero phase
                if (tutorialPhase === 'gacha') {
                    // Create the actual Item object for the tutorial
                    const tutItem: Item = {
                        id: `loot_${Date.now()}_tut`,
                        name: actualItem.name,
                        emoji: actualItem.emoji,
                        slot: determineSlot(actualItem),
                        power: actualItem.power,
                        rarity: actualItem.rarity as ItemRarity
                    }
                    setTutorialWonItem(tutItem)
                    setTutorialSubStep(2)
                }
            }, 3500)
        } else {
            // 10x pull: keep existing card reveal system
            setIsOpening(true)
            setShowResult(false)
            setResults([])
            setRevealedIndex(0)

            // Apply rewards
            lootItems.forEach(loot => {
                if (loot.power > 0) {
                    const item: Item = {
                        id: `loot_${Date.now()}_${Math.random()}`,
                        name: loot.name,
                        emoji: loot.emoji,
                        slot: determineSlot(loot),
                        power: loot.power,
                        rarity: loot.rarity as ItemRarity
                    }
                    addItem(item)
                }
                if (loot.gold) addGold(loot.gold)
            })

            // Animation timing
            setTimeout(() => {
                setIsOpening(false)
                setResults(lootItems)
                setShowResult(true)

                // Reveal items one by one
                lootItems.forEach((_, idx) => {
                    setTimeout(() => setRevealedIndex(idx + 1), idx * 300)
                })
            }, 2500)
        }
    }

    const handleSinglePull = () => {
        if (freeLootboxSpins > 0) {
            if (useFreeLootboxSpin()) performPull(1)
        } else if (spendGems(singlePullCost)) {
            performPull(1)
        }
    }

    const handleTenPull = () => {
        if (spendGems(tenPullCost)) performPull(10)
    }

    const handlePullAgain = () => {
        setShowResult(false)
        if (lastPullType === 1) {
            if (freeLootboxSpins > 0) {
                if (useFreeLootboxSpin()) performPull(1)
            } else if (resources.gems >= singlePullCost) {
                if (spendGems(singlePullCost)) performPull(1)
            }
        } else if (lastPullType === 10 && resources.gems >= tenPullCost) {
            if (spendGems(tenPullCost)) performPull(10)
        }
    }

    const closeResult = () => {
        setShowResult(false)
        setResults([])

        // Tutorial: Advance to hero phase
        if (tutorialPhase === 'gacha') {
            setTutorialPhase('hero')
            setScreen('hero')
        }
    }

    const pityProgress = (gachaPity % 100)
    const canPullAgain = lastPullType === 1
        ? (freeLootboxSpins > 0 || resources.gems >= singlePullCost)
        : resources.gems >= tenPullCost

    // Get best rarity from results
    const bestRarity = results.length > 0
        ? results.reduce((best, item) => {
            const order = ['common', 'uncommon', 'rare', 'epic', 'legendary']
            return order.indexOf(item.rarity) > order.indexOf(best.rarity) ? item : best
        }, results[0])
        : null

    return (
        <GameLayout>
            <div className="gacha">
                {/* Header */}
                <div className="gacha__header">
                    <button className="gacha__back" onClick={() => setScreen('city')}>← Zurück</button>
                    <div className="gacha__title">
                        <h1>✨ SCHATZKAMMER</h1>
                        <span className="gacha__title-sub">Öffne Boxen für Belohnungen!</span>
                    </div>
                </div>

                {/* Main Box - Clickable for 1x pull */}
                <div
                    className={`gacha__hero ${isOpening ? 'gacha__hero--opening' : ''} ${!isOpening && (freeLootboxSpins > 0 || resources.gems >= singlePullCost) ? 'gacha__hero--clickable' : ''}`}
                    onClick={() => !isOpening && (freeLootboxSpins > 0 || resources.gems >= singlePullCost) && handleSinglePull()}
                >
                    <div className="gacha__hero-bg" />
                    <div className="gacha__hero-shine" />
                    <div className="gacha__box">
                        {!isOpening ? (
                            <span className="gacha__box-emoji">🎁</span>
                        ) : (
                            <span className="gacha__spinner">✨</span>
                        )}
                    </div>
                    <span className="gacha__hint">
                        {isOpening ? 'Schatz wird gehoben...' : 'Tippe auf die Truhe!'}
                    </span>
                </div>

                {/* Pull Buttons */}
                <div className="gacha__pulls">
                    <button
                        id="gacha-pull-btn"
                        className="btn btn--gold btn--lg btn--shine gacha__pull-btn-content"
                        onClick={handleSinglePull}
                        disabled={(freeLootboxSpins === 0 && resources.gems < singlePullCost) || isOpening}
                    >
                        <span className="gacha__pull-main-text">🎲 1× Öffnen</span>
                        {freeLootboxSpins > 0 ? (
                            <span className="gacha__pull-subtext">🎁 GRATIS ({freeLootboxSpins})</span>
                        ) : (
                            <span className="gacha__pull-subtext">💎 {singlePullCost}</span>
                        )}
                    </button>

                    <button
                        className="btn btn--gold btn--lg btn--shine btn--ten-pull gacha__pull-btn-content"
                        onClick={handleTenPull}
                        disabled={resources.gems < tenPullCost || isOpening}
                    >
                        <span className="gacha__pull-main-text">🎰 10× Öffnen</span>
                        <span className="gacha__pull-subtext">💎 {tenPullCost}</span>
                    </button>
                </div>

                {/* Shop CTA - Repositioned for visibility */}
                <div className="gacha__shop-cta-container">
                    <button className="gacha__shop-cta" onClick={() => {
                        setShopTab('packs')
                        setScreen('shop')
                    }}>
                        <span className="gacha__shop-text">✨ Mehr Gems für mehr Boxen!</span>
                        <span className="gacha__shop-arrow">→</span>
                    </button>
                </div>

                {/* Pity Section */}
                <div className="gacha__pity-section">
                    <div className="gacha__pity-info">
                        <span className="gacha__pity-label">✨ GLÜCKS-GARANTIE</span>
                        <span className="gacha__pity-value">{pityProgress}%</span>
                    </div>
                    <div className="gacha__pity-bar">
                        <div className="gacha__pity-fill" style={{ width: `${pityProgress}%` }} />
                        <span className="gacha__pity-marker">🌟</span>
                    </div>
                    <span className="gacha__pity-hint">
                        {pityProgress < 100
                            ? `Noch ${100 - pityProgress} Ziehungen bis zum garantierten legendären Item!`
                            : 'DEINE NÄCHSTE ZIEHUNG IST LEGENDÄR!'}
                    </span>
                </div>

                {/* Drop Rates */}
                <div className="gacha__rates-section">
                    <span className="gacha__rates-title">Wahrscheinlichkeiten</span>
                    <div className="gacha__rates-grid">
                        <div className="gacha__rate" style={{ '--rate-color': RARITY_COLORS.legendary } as React.CSSProperties}>
                            <span className="gacha__rate-name">Legendär</span>
                            <span className="gacha__rate-pct">{(GAME_CONFIG.gacha.dropRates.legendary * 100).toFixed(1)}%</span>
                        </div>
                        <div className="gacha__rate" style={{ '--rate-color': RARITY_COLORS.epic } as React.CSSProperties}>
                            <span className="gacha__rate-name">Episch</span>
                            <span className="gacha__rate-pct">{(GAME_CONFIG.gacha.dropRates.epic * 100).toFixed(1)}%</span>
                        </div>
                        <div className="gacha__rate" style={{ '--rate-color': RARITY_COLORS.rare } as React.CSSProperties}>
                            <span className="gacha__rate-name">Selten</span>
                            <span className="gacha__rate-pct">{(GAME_CONFIG.gacha.dropRates.rare * 100).toFixed(0)}%</span>
                        </div>
                        <div className="gacha__rate" style={{ '--rate-color': RARITY_COLORS.uncommon } as React.CSSProperties}>
                            <span className="gacha__rate-name">Extra</span>
                            <span className="gacha__rate-pct">{(GAME_CONFIG.gacha.dropRates.uncommon * 100).toFixed(0)}%</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Result Modal */}
            {showResult && results.length > 0 && (
                <div className="gacha__result-overlay" onClick={closeResult}>
                    <div
                        className={`gacha__result-container ${results.length > 1 ? 'gacha__result-container--multi' : ''}`}
                        onClick={e => e.stopPropagation()}
                        style={{ '--best-rarity-color': bestRarity ? RARITY_COLORS[bestRarity.rarity] : RARITY_COLORS.common } as React.CSSProperties}
                    >
                        {/* Single Result */}
                        {results.length === 1 && (
                            <div className="gacha__single-result">
                                <h2 className="gacha__multi-title">🎉 SCHATZ GEFUNDEN!</h2>
                                <div
                                    className="gacha__result-card"
                                    style={{ '--rarity-color': RARITY_COLORS[results[0].rarity] } as React.CSSProperties}
                                >
                                    <div className="gacha__result-glow"></div>
                                    <span className="gacha__result-emoji">{results[0].emoji}</span>
                                    <h2 className="gacha__result-name">{results[0].name}</h2>
                                    <span className="gacha__result-rarity">{RARITY_NAMES[results[0].rarity]}</span>
                                </div>
                            </div>
                        )}

                        {/* Multi Result Grid */}
                        {results.length > 1 && (
                            <div className="gacha__multi-results">
                                <h2 className="gacha__multi-title">🎉 10× SCHÄTZE GEHOBEN!</h2>
                                <div className="gacha__multi-grid">
                                    {results.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`gacha__multi-item ${idx < revealedIndex ? 'gacha__multi-item--revealed' : ''}`}
                                            style={{
                                                '--rarity-color': RARITY_COLORS[item.rarity],
                                                '--delay': `${idx * 0.1}s`
                                            } as React.CSSProperties}
                                        >
                                            <span className="gacha__multi-emoji">{item.emoji}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="gacha__multi-summary">
                                    <span>+{results.reduce((s, i) => s + i.power, 0)} ⚔️</span>
                                    <span>+{results.reduce((s, i) => s + (i.gold || 0), 0)} 💰</span>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="gacha__result-actions">
                            {canPullAgain && tutorialPhase !== 'gacha' && (
                                <button
                                    className={`btn btn--lg btn--full btn--shine ${lastPullType === 10 ? 'btn--ten-pull' : 'btn--gold'}`}
                                    onClick={handlePullAgain}
                                >
                                    🔄 Nochmal {lastPullType}× ({lastPullType === 1 && freeLootboxSpins > 0 ? 'GRATIS' : `${lastPullType === 1 ? singlePullCost : tenPullCost} 💎`})
                                </button>
                            )}
                            <button id="gacha-accept-btn" className="btn btn--ghost btn--lg btn--full" onClick={closeResult}>
                                Annehmen
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Slot Machine */}
            {showSlotMachine && (
                <div className="gacha__slot-overlay">
                    <div className="gacha__slot-container">
                        {/* Center Marker */}
                        <div className="gacha__slot-marker" />

                        {/* Scrolling Band */}
                        <div
                            className={`gacha__slot-band ${isSpinning ? 'gacha__slot-band--spinning' : ''}`}
                            style={{
                                transform: `translateX(${bandTransform}px)`,
                            }}
                        >
                            {slotBand.map((item, idx) => (
                                <div
                                    key={idx}
                                    className={`gacha__slot-item gacha__slot-item--${item.rarity}`}
                                >
                                    <span className="gacha__slot-emoji">{item.emoji}</span>
                                    <span className="gacha__slot-name">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </GameLayout>
    )
}
